import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Gift, TreePine, TrendingUp, CheckCircle, Leaf } from "lucide-react";
import { usePartnerViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import LeadForm from "@/forms/engine/LeadForm";
import referral from "@/forms/definitions/referral";
import RelatedReviews from "@/components/RelatedReviews";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import { useEcologi } from "@/components/EcologiBadge";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const viewportOnce = { once: true, amount: 0 };
const heroVisualInitial = { opacity: 0, y: 20 };
const heroVisualAnimate = { opacity: 1, y: 0 };
const heroVisualTransition = { duration: 0.5, delay: 0.3 };

export default function ReferAFriendPage() {
  usePartnerViewed("refer_a_friend", { itemType: "partner_service" });
  const eco = useEcologi();

  return (
    <div>
      <SEOHead title="Refer a Friend & Earn Rewards" description="Recommend our merchant services to other businesses with the CanDo Payments refer a friend scheme. Share the benefits and earn exclusive rewards today." path="/refer-a-friend" />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Refer A Friend</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Know a business that takes payments? <span className="gradient-text">They&apos;ll thank you for this.</span></motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">Refer a business to CanDo Payments and everyone wins. They save money on their card processing, you earn a reward, and we plant a tree through Ecologi. No forms, no hassle — just an introduction.</motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <a href="#refer-form" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-all btn-shine">Refer a business <ArrowRight size={16} /></a>
              </motion.div>
            </motion.div>
            <motion.div initial={heroVisualInitial} animate={heroVisualAnimate} transition={heroVisualTransition} className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-xl border border-brand-light-grey/60 p-6 max-w-sm mx-auto">
                <p className="text-[10px] uppercase tracking-widest text-brand-grey mb-1">Everyone wins</p>
                <div className="space-y-4 mt-4">
                  {[
                    { label: "You", result: "Earn a thank-you reward", colour: "text-brand-aqua-dark" },
                    { label: "Your friend", result: "Saves 20–64% on processing", colour: "text-brand-green" },
                    { label: "The planet", result: "A tree planted in your name", colour: "text-green-600" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between text-sm border-t border-brand-light-grey/60 pt-3">
                      <span className="text-brand-grey">{item.label}</span>
                      <span className={`font-bold ${item.colour}`}>{item.result}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">How It Works</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Three steps. Everyone benefits.</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: "You earn a reward", desc: "Every successful referral earns you a thank-you reward. The more you refer, the more you earn. There's no limit." },
              { icon: Gift, title: "They save money", desc: "Your friend gets honest pricing, real support, and typically saves 20–64% on their current processing costs." },
              { icon: TreePine, title: "We plant a tree", desc: "Every referral means a tree planted through Ecologi. Good for business, good for the planet." },
            ].map((item) => (
              <div key={item.title} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6 text-center">
                <item.icon size={28} className="text-brand-aqua mx-auto mb-4" />
                <h3 className="font-heading font-bold text-brand-black mb-2">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecologi stat */}
      <section className="py-8 bg-brand-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-6 text-center">
          <Leaf size={20} className="text-green-400" />
          <p className="text-sm"><strong className="text-green-300">{`${eco.trees > 0 ? eco.trees.toLocaleString() : "..."} trees`}</strong> planted so far through referrals. <a href={eco.profile_url} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 underline">See our forest →</a></p>
        </div>
      </section>

      {/* Form */}
      <section id="refer-form" className="py-16 md:py-20 bg-gray-50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Make a Referral</p>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Tell us who to call</h2>
            <p className="text-brand-grey leading-relaxed mb-6">Just give us their name and a way to reach them. We&apos;ll handle the rest — no pressure, no hard sell. If they switch, you get rewarded.</p>
            <div className="space-y-3">
              {[
                "No sales experience needed — just an introduction",
                "We'll never cold-call or pressure your contact",
                "You'll be kept updated every step of the way",
                "Reward paid once the referral goes live",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                  <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                </div>
              ))}
            </div>
            <div className="mt-6 text-sm text-brand-grey">
              <strong>Looking for a more formal partnership?</strong>{" "}
              <Link to="/partner-programme" className="text-brand-aqua hover:underline font-semibold">Explore our Partner Programme →</Link>
            </div>
          </div>
          <div className="space-y-5">
            <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
              <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Refer a business</h3>
              <p className="text-sm text-brand-grey mb-4">Tell us about the business you&apos;d like to refer and we&apos;ll take it from there.</p>
              <LeadForm schema={referral} />
            </div>
            <FormReviewCompanion industry="partnership" product="partners" label="What our partners say" testId="refer-a-friend-form-review" />
          </div>
        </div>
      </section>

      <RelatedReviews
        industry="partnership"
        product="partners"
        eyebrow="Partner Stories"
        heading="See what our referral partners say"
        subheading="Real feedback from agencies, developers and friends of CanDo who referred a business."
        testId="refer-a-friend-related-reviews"
      />
    </div>
  );
}
